import React, { useMemo, useState, useRef, useEffect } from "react";
import {
  Card, Row, Col, Space, Button, Table, Tag, Tooltip, Modal, Form,
  Input, message, Typography, Descriptions, Radio, Select, InputNumber, DatePicker,
  Alert
} from "antd";
import {
  PlusOutlined, ReloadOutlined, SearchOutlined, FilterOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useFragmentation } from "./hooks/useFragmentation";
import FileUploadField from "./Attributes/FileUploadField";
import { fileNameError, isValidFileName } from "../util/fileNameValidation";
import DownloadIconButton from "./shared/DownloadIconButton";

const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const STATUS_COLORS = { "in progress": "processing", "queued": "default", "pending": "default", "completed": "success", "failed": "error" };
const DEFAULT_CUSTOM_SMARTS = "[!#0]-!@[!#0]";

const customFormStyles = `
  .suppress-form-item-errors .ant-form-item-explain {
    display: none;
  }
`;

export function FragmentationTab({ projectId, active }) {
  const [form] = Form.useForm();
  const fileInputRef = useRef(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  
  const [showFilters, setShowFilters] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [dateRange, setDateRange] = useState([]);
  
  const {
    loading, rows, isSubmitting, isSubmitModalVisible, setSubmitModalVisible, isViewModalVisible,
    setViewModalVisible, selectedRecord, setSelectedRecord, fetchRows, handleDownloadInputFile,
    handleDownloadOutputFile, handleSubmit, handleUpload, isSuccessModalVisible, setSuccessModalVisible, successData
  } = useFragmentation(projectId, active);

  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [formErrors, setFormErrors] = useState([]);
  
  // Watch specific fields that affect validation
  const fragmenterFile = Form.useWatch("fragmenter_file", form);
  const rxnSmartsSelect = Form.useWatch("rxnSMARTS_select", form);
  const rxnSmartsCustom = Form.useWatch("rxnSMARTS_custom", form);
  const relPopThreshold = Form.useWatch("rel_pop_threshold", form);
  const flattenChirality = Form.useWatch("flattenChirality", form);
  const outputFileTag = Form.useWatch("output_file_tag", form);

  const distinctUsers = useMemo(() => Array.from(new Set(rows.map(r => r.submittedBy).filter(Boolean))), [rows]);
  const distinctStatuses = useMemo(() => Array.from(new Set(rows.map(r => r.status).filter(Boolean))), [rows]);

  // Validate form whenever watched fields change
  useEffect(() => {
    if (!isSubmitModalVisible) return;

    form.validateFields({ validateOnly: true })
      .then(() => {
        setIsSubmitDisabled(false);
        setFormErrors([]);
      })
      .catch((errorInfo) => {
        setIsSubmitDisabled(true);
        const messages = errorInfo.errorFields.flatMap(field => field.errors).filter(Boolean);
        setFormErrors(messages);
      });
  }, [
    isSubmitModalVisible,
    fragmenterFile,
    rxnSmartsSelect,
    rxnSmartsCustom,
    relPopThreshold,
    flattenChirality,
    outputFileTag,
    form
  ]);

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!isValidFileName(file.name)) {
        message.error(fileNameError(file.name));
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      const lowerName = file.name.toLowerCase();
      if (!lowerName.endsWith(".smi") && !lowerName.endsWith(".sdf")) {
        message.error("Only .smi and .sdf files are allowed.");
        return;
      }
      const uploadedFileKey = await handleUpload(file);
      if (uploadedFileKey) {
        form.setFieldsValue({ fragmenter_file: uploadedFileKey });
      }
      if(fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRxnSmartsChange = (value) => {
    if (value === "custom") {
      form.setFieldsValue({ rxnSMARTS_custom: DEFAULT_CUSTOM_SMARTS });
      // Trigger validation after setting the value
      setTimeout(() => {
        form.validateFields({ validateOnly: true })
          .then(() => {
            setIsSubmitDisabled(false);
            setFormErrors([]);
          })
          .catch((errorInfo) => {
            setIsSubmitDisabled(true);
            const messages = errorInfo.errorFields.flatMap(field => field.errors).filter(Boolean);
            setFormErrors(messages);
          });
      }, 0);
    } else {
      // Clear custom field when switching back to BRICS
      form.setFieldsValue({ rxnSMARTS_custom: undefined });
    }
  };

  const handleViewDetails = React.useCallback((record) => {
    setSelectedRecord(record);
    setViewModalVisible(true);
  }, [setSelectedRecord, setViewModalVisible]);
  
  const filteredRows = useMemo(() => {
    return rows.filter(row => {
      const txt = searchText.toLowerCase();
      if (txt && ![row.id, row.inputFile, row.description, row.submittedBy, row.status].some(f => f?.toString().toLowerCase().includes(txt))) return false;
      if (selectedUsers.length && !selectedUsers.includes(row.submittedBy)) return false;
      if (selectedStatuses.length && !selectedStatuses.includes(row.status)) return false;
      if (dateRange?.length === 2 && row.submittedTimestamp) {
        const d = dayjs(row.submittedTimestamp);
        if (d.isBefore(dateRange[0], "day") || d.isAfter(dateRange[1], "day")) return false;
      }
      return true;
    });
  }, [rows, searchText, selectedUsers, selectedStatuses, dateRange]);
  
  const columns = useMemo(() => [
    { 
      title: "Fragmenter ID", 
      dataIndex: "id", 
      key: "id", 
      width: 130, 
      sorter: (a, b) => a.id.localeCompare(b.id), 
      render: (text, record) => (
        <Button type="link" onClick={() => handleViewDetails(record)} style={{ padding: 0 }}>
          <Text code>{text}</Text>
        </Button>
      )
    },
    { 
      title: "Input File", 
      dataIndex: "inputFile", 
      key: "inputFile",
      width: 170,
      ellipsis: true,
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
          <Text ellipsis={{ tooltip: text }} style={{ flex: 1, minWidth: 0 }}>
            {text || "-"}
          </Text>
          <DownloadIconButton
            title={text ? `Download input file: ${text}` : "No input file"}
            disabled={!text}
            onClick={() => handleDownloadInputFile(record)}
          />
        </div>
      )
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 220,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text || "-"}>
          <span>{text || "-"}</span>
        </Tooltip>
      ),
    },
    { title: "Submitted By", dataIndex: "submittedBy", key: "submittedBy", width: 230, sorter: (a, b) => a.submittedBy.localeCompare(b.submittedBy) },
    { title: "Timestamp", dataIndex: "submittedTimestamp", key: "submittedTimestamp", width: 230, sorter: (a, b) => dayjs(a.submittedTimestamp).unix() - dayjs(b.submittedTimestamp).unix(), render: (t) => t ? dayjs(t).format("YYYY-MM-DD HH:mm:ss") : "—" },
    { title: "Status", dataIndex: "status", key: "status", width: 140, sorter: (a, b) => a.status.localeCompare(b.status), render: (s) => <Tag color={STATUS_COLORS[String(s).toLowerCase()] || "default"}>{s || "—"}</Tag> },
    { 
      title: "Result File", 
      key: "download", 
      width: 120, 
      align: "center", 
      render: (_, rec) => {
        const isFailed = rec.status?.toLowerCase() === "failed";
        return (
          <DownloadIconButton
            title={rec.outputFile ? `Download ${rec.outputFile}` : "No output file"}
            disabledTitle={isFailed ? "Download unavailable for failed jobs" : "No output file"}
            disabled={!rec.outputFile || isFailed}
            onClick={() => handleDownloadOutputFile(rec)}
          />
        );
      },
    }
  ], [handleDownloadInputFile, handleDownloadOutputFile, handleViewDetails]);

  return (
    <>
      <style>{customFormStyles}</style>
      <Card>
        <Row gutter={16} align="middle" style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8} md={6}>
            <Input placeholder="Search jobs..." value={searchText} onChange={e => setSearchText(e.target.value)} prefix={<SearchOutlined />} allowClear />
          </Col>
          <Col flex="auto" />
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={fetchRows} loading={loading} />
              <Button icon={<FilterOutlined />} onClick={() => setShowFilters(prev => !prev)} />
              <Button onClick={() => { setSearchText(""); setSelectedUsers([]); setSelectedStatuses([]); setDateRange([]); }}>Clear Filters</Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setSubmitModalVisible(true)}>Create Fragment Library</Button>
            </Space>
          </Col>
        </Row>
        
        {showFilters && (
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={8}><Select mode="multiple" allowClear style={{ width: "100%" }} placeholder="Filter by user" value={selectedUsers} onChange={setSelectedUsers} options={distinctUsers.map(u => ({label: u, value: u}))} /></Col>
            <Col xs={24} sm={8}><Select mode="multiple" allowClear style={{ width: "100%" }} placeholder="Filter by status" value={selectedStatuses} onChange={setSelectedStatuses} options={distinctStatuses.map(s => ({label: s, value: s}))} /></Col>
            <Col xs={24} sm={8}><RangePicker style={{ width: "100%" }} value={dateRange} onChange={setDateRange} /></Col>
          </Row>
        )}

        <Table rowKey="id" loading={loading} dataSource={filteredRows} columns={columns} bordered size="small" tableLayout="fixed" scroll={{ x: 1250 }} pagination={{ pageSize: 10, total: filteredRows.length, showSizeChanger: true }} />

        <Modal 
          title="Submit New Fragmentation" 
          open={isSubmitModalVisible} 
          onOk={() => handleSubmit(form, uploadedFile)} 
          onCancel={() => setSubmitModalVisible(false)} 
          okText="Submit"
          okButtonProps={{ loading: isSubmitting, disabled: isSubmitDisabled }}
          destroyOnClose 
          afterClose={() => { 
            form.resetFields(); 
            setUploadedFile(null); 
            setFormErrors([]);
            setIsSubmitDisabled(true);
          }}
          width={620}
        >
          <Form 
            form={form} 
            layout="vertical" 
            name="fragmentation-submit-form" 
            initialValues={{ rxnSMARTS_select: "brics", rel_pop_threshold: 1.00, flattenChirality: true }}
            className="suppress-form-item-errors"
          >
            <Form.Item name="fragmenter_file" label="Input SMI/SDF File" rules={[{ required: true, message: "Please select or upload a file"}]}>
              <FileUploadField projectId={projectId} fileType="Fragmenterfiles" accept=".smi,.sdf" maxSizeMB={10} />
            </Form.Item>
            <div style={{ marginTop: -12, marginBottom: 16, color: "#8c8c8c", fontSize: 12 }}>
              Supported formats: .smi, .sdf
            </div>
            
            <input type="file" ref={fileInputRef} onChange={handleFileSelected} style={{ display: "none" }} accept=".smi,.sdf" />
            
            <Form.Item label="Bond Pattern SMARTS" required tooltip="Bond patterns to break">
              <Input.Group compact>
                <Form.Item name="rxnSMARTS_select" noStyle>
                  <Select style={{ width: "40%" }} onChange={handleRxnSmartsChange}>
                    <Option value="brics">BRICS</Option>
                    <Option value="custom">Bond Pattern SMARTS</Option>
                  </Select>
                </Form.Item>
                {rxnSmartsSelect === "custom" && (
                  <Form.Item 
                    name="rxnSMARTS_custom" 
                    noStyle 
                    rules={[{ required: true, message: "A custom rxnSMARTS string is required" }]}
                  >
                    <Input style={{ width: "60%" }} placeholder="Enter SMARTS string"/>
                  </Form.Item>
                )}
              </Input.Group>
            </Form.Item>

            <Form.Item name="description" label="Description (Optional)">
              <Input.TextArea rows={2} placeholder="Enter a short description for this job" maxLength={250} showCount />
            </Form.Item>
            
            <Row gutter={16} align="top">
              <Col span={8}>
                <Form.Item name="output_file_tag" label="Result File Tag" rules={[{ pattern: /^[a-zA-Z0-9_-]+$/, message: "Tag can only contain letters, numbers, _, -" }]}>
                  <Input 
                    placeholder="my_run_tag" 
                    maxLength={20} 
                    showCount 
                    onChange={(e) => {
                      const { value } = e.target;
                      form.setFieldsValue({ output_file_tag: value.replace(/\s/g, "") });
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item name="rel_pop_threshold" label="Relative population threshold" rules={[ { required: true, message: "Threshold is required." }, { type: "number", min: 0.00, max: 10.00, message: "Threshold must be between 0.00 and 10.00." } ]}>
                  <InputNumber step={0.01} precision={2} addonAfter="%" style={{width: "100%"}} />
                </Form.Item>   
              </Col>
              <Col span={6}>
                <Form.Item name="flattenChirality" label="Flatten Chirality" rules={[{ required: true, message: "Please select an option" }]}>
                  <Radio.Group><Radio value={true}>True</Radio><Radio value={false}>False</Radio></Radio.Group>
                </Form.Item>
              </Col>
            </Row>
          </Form>

          <div style={{ minHeight: "50px", marginTop: "16px", borderTop: "1px solid #f0f0f0", paddingTop: "16px" }}>
            {outputFileTag && (
              <Text type="secondary" style={{ display: "block", marginBottom: formErrors.length > 0 ? "12px" : 0 }}>
                Result file will be: &lt;fragmenter_id&gt;_{outputFileTag}.json
              </Text>
            )}
            {formErrors.length > 0 && (
              <Alert
                type="error"
                message={
                  <ul style={{ margin: 0, paddingLeft: "20px" }}>
                    {formErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                }
              />
            )}
          </div>
        </Modal>

        <Modal title="Fragmentation Job Details" open={isViewModalVisible} onCancel={() => setViewModalVisible(false)} footer={[<Button key="close" onClick={() => setViewModalVisible(false)}>Close</Button>]}>
          {selectedRecord && (
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Fragmenter ID"><Text code>{selectedRecord.id}</Text> <Tag color={STATUS_COLORS[String(selectedRecord.status).toLowerCase()] || "default"}>{selectedRecord.status}</Tag></Descriptions.Item>
              <Descriptions.Item label="Description">{selectedRecord.inputParams?.description?.trim() || "-"}</Descriptions.Item>
              <Descriptions.Item label="Input File">{selectedRecord.inputParams?.fragmenter_file || "N/A"}</Descriptions.Item>
              <Descriptions.Item label="Bond Pattern SMARTS">{selectedRecord.inputParams?.rxnSMARTS || "N/A"}</Descriptions.Item>
              <Descriptions.Item label="Flatten Chirality">{String(selectedRecord.inputParams?.flattenChirality)}</Descriptions.Item>
              <Descriptions.Item label="Result File">{selectedRecord.outputFile || "N/A"}</Descriptions.Item>
              <Descriptions.Item label="Status Message">{selectedRecord.statusMessage}</Descriptions.Item>
              <Descriptions.Item label="Submitted">{dayjs(selectedRecord.submittedTimestamp).format("YYYY-MM-DD HH:mm:ss")} by {selectedRecord.submittedBy}</Descriptions.Item>
              <Descriptions.Item label="Completed">{selectedRecord.completedTimestamp ? dayjs(selectedRecord.completedTimestamp).format("YYYY-MM-DD HH:mm:ss") : "—"}</Descriptions.Item>
            </Descriptions>
          )}
        </Modal>

        <Modal 
          title="Submission Successful"
          open={isSuccessModalVisible}
          onOk={() => setSuccessModalVisible(false)}
          onCancel={() => setSuccessModalVisible(false)}
          footer={[ <Button key="ok" type="primary" onClick={() => setSuccessModalVisible(false)}>OK</Button> ]}
        >
          {successData && (
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Message">{successData.message}</Descriptions.Item>
              <Descriptions.Item label="Fragmenter ID"><Text code>{successData.fragmenter_id}</Text></Descriptions.Item>
              <Descriptions.Item label="Result File">{successData.result_file || "N/A"}</Descriptions.Item>
              <Descriptions.Item label="Submitted By">{successData.submitted_id}</Descriptions.Item>
              <Descriptions.Item label="Submitted Time">{dayjs(successData.submitted_time).format("YYYY-MM-DD HH:mm:ss")}</Descriptions.Item>
            </Descriptions>
          )}
        </Modal>
      </Card>
    </>
  );
}
